import { useEffect, useState } from "react";

type DataObserver = {
  data?: unknown;
  enabled?: boolean;
  error?: unknown;
  hasSettled?: boolean;
  isLoading?: boolean;
};

type UseDataReadyOptions = {
  minimumDelayMs?: number;
};

export function useDataReady(
  observers: DataObserver[],
  opts: UseDataReadyOptions = {},
) {
  const readyNow = isDataReady(observers);
  const minimumDelayMs = Math.max(0, opts.minimumDelayMs ?? 0);
  const [hasBeenReady, setHasBeenReady] = useState(
    () => readyNow && minimumDelayMs === 0,
  );

  useEffect(() => {
    if (!readyNow || hasBeenReady) return;
    const timer = window.setTimeout(() => {
      setHasBeenReady(true);
    }, minimumDelayMs);
    return () => window.clearTimeout(timer);
  }, [hasBeenReady, minimumDelayMs, readyNow]);

  return hasBeenReady || (readyNow && minimumDelayMs === 0);
}

export function isDataReady(observers: DataObserver[]) {
  const activeObservers = observers.filter(
    (observer) => observer.enabled !== false,
  );
  if (activeObservers.length === 0) return true;

  const hasOutcome = activeObservers.some((observer) =>
    hasObserverOutcome(observer),
  );
  const allSettled = activeObservers.every(
    (observer) =>
      hasObserverOutcome(observer) ||
      observer.hasSettled === true ||
      observer.isLoading === false,
  );
  return hasOutcome && allSettled;
}

function hasObserverOutcome(observer: DataObserver) {
  return observer.data != null || observer.error != null;
}
